import type { ProductSearchQuery } from '@scayle/storefront-nuxt'
import type { FilterItemWithValues } from '#layers/base/types/filter'

/*
  Builds a comma-separated list of the active filter *names* for the
  `item_list_filter` tracking parameter.

  ECOM-2675: the previous implementation joined every URL query key, which
  leaked UTM and other non-filter params. Names are derived from the parsed
  `appliedFilter` and intersected with the canonical SCAYLE filter list
  (`filters[].slug`) so only real product filters are reported. `term` is
  intentionally omitted to preserve existing behaviour.

  Eg. 'minPrice,maxPrice,colorRange,sale'

  @param filters - The available (post-sanitize) filters from the SCAYLE filter
    API. Used to gate attribute filters.
  @param appliedFilter - The parsed product search query
  @param unfilteredFilters - The unfiltered category filter universe. Used to
    gate boolean filters, since `filters` drops a boolean whose `true` count is
    0 after the current filter combination (see below).
  @returns The filter names string
*/
export const getFilterNames = (
  filters: FilterItemWithValues[] | null | undefined,
  appliedFilter: ProductSearchQuery,
  unfilteredFilters?: FilterItemWithValues[] | null,
) => {
  const { attributes, minPrice, maxPrice } = appliedFilter
  const names = new Set<string>()

  // Presence checks, not truthiness: a valid `minPrice=0` lower bound must
  // still be reported (useAppliedFilters parses it to 0 and counts bounds by
  // `!== undefined`). NB: getFilterOptions intentionally uses truthiness here,
  // so `minPrice=0` appears in item_list_filter but not item_list_filter_options
  // — that asymmetry is accepted (item_list_filter reports presence, options
  // reports values).
  if (minPrice !== undefined) {
    names.add('minPrice')
  }
  if (maxPrice !== undefined) {
    names.add('maxPrice')
  }

  const attributeSlugs = new Set(
    Array.isArray(filters) ? filters.map((filter) => filter.slug) : [],
  )

  // Booleans are gated against the *unfiltered* filter universe, not the
  // post-sanitize `filters` list. `filters` (availableFilters) drops a boolean
  // whose `true` value has zero matching products *after the current filter
  // combination* (e.g. `sale` once other filters leave no sale items), but an
  // active `?sale=true` must still be reported. The unfiltered set still
  // contains `sale` whenever the category has any sale products, so gating
  // against it keeps real booleans while rejecting non-filter true/false params
  // (feature toggles like `?htf.x=false`, consent keys) that never appear as
  // SCAYLE filter slugs. Falls back to `filters` when the unfiltered set is
  // unavailable.
  const booleanSlugs = new Set([
    ...attributeSlugs,
    ...(Array.isArray(unfilteredFilters)
      ? unfilteredFilters.map((filter) => filter.slug)
      : []),
  ])

  attributes?.forEach((attribute) => {
    if (!attribute.key) {
      return
    }
    const isAllowed =
      attribute.type === 'boolean'
        ? booleanSlugs.has(attribute.key)
        : attributeSlugs.has(attribute.key)
    if (isAllowed) {
      names.add(attribute.key)
    }
  })

  return [...names].join(',')
}
